import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Paper, Box, Grid, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { formatDistanceToNowLocalized } from "shell/i18n/dates";
import { isValid } from "date-fns";
import { NoResults } from "../../../../../schema/src/app/components/NoResults";
import { FileTypes } from "../constants";
export type FileProps = {
  icon?: React.ElementType | null;
  fileName: string;
  lastSaved: string;
  path?: string | null;
  ZUID?: string;
  fileType?: string;
  type?: FileTypes;
};
type FileListProps = {
  files: FileProps[];
  searchKeyword?: string;
  searchInputRef?: React.RefObject<HTMLInputElement>;
  onClearSearch?: () => void;
};
const FileRowItem: FC<FileProps & {
  isLast: boolean;
}> = ({
  icon: Icon,
  fileName,
  lastSaved,
  path,
  isLast
}) => {
  return <Box data-cy="AllFilesRow" component={path ? Link : "div"} to={path || undefined} px={2} width="100%" height={56} display="flex" alignItems="center" sx={{
    borderBottom: isLast ? "none" : "1px solid",
    borderColor: "grey.700",
    textDecoration: "none",
    color: "grey.500",
    "&:hover": {
      backgroundColor: "grey.900"
    }
  }} data-sentry-element="Box" data-sentry-component="FileRowItem" data-sentry-source-file="FileList.tsx">
      <Grid container width="100%" data-sentry-element="Grid" data-sentry-source-file="FileList.tsx">
        <Grid size={8} display="flex" alignItems="center" gap={1.5} data-sentry-element="Grid" data-sentry-source-file="FileList.tsx">
          {Icon && <Icon sx={{
          width: 20,
          height: 20,
          color: "grey.500"
        }} />}
          <Typography variant="body1" color="common.white" sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          pr: 2.5
        }} data-sentry-element="Typography" data-sentry-source-file="FileList.tsx">
            {fileName}
          </Typography>
        </Grid>
        <Grid size={4} data-sentry-element="Grid" data-sentry-source-file="FileList.tsx">
          <Typography data-cy="AllFilesRowLastSaved"
        // this is used for cypress testing
        // determine the list order
        data-last-update={isValid(new Date(lastSaved)) ? new Date(lastSaved).getTime() : "-"} variant="body1" color="common.white" data-sentry-element="Typography" data-sentry-source-file="FileList.tsx">
            {isValid(new Date(lastSaved)) ? formatDistanceToNowLocalized(new Date(lastSaved), {
            addSuffix: true
          }) : "—"}
          </Typography>
        </Grid>
      </Grid>
    </Box>;
};
const FileListHeader = () => {
  const {
    t
  } = useTranslation();
  return <Box pl={2} pr={3.75} width="100%" height={56} display="flex" alignItems="center" sx={{
    borderBottom: "1px solid",
    borderColor: "grey.700"
  }} data-sentry-element="Box" data-sentry-component="FileListHeader" data-sentry-source-file="FileList.tsx">
      <Grid container width="100%" data-sentry-element="Grid" data-sentry-source-file="FileList.tsx">
        <Grid size={8} data-sentry-element="Grid" data-sentry-source-file="FileList.tsx">
          <Typography variant="h6" fontWeight={700} color="common.white" data-sentry-element="Typography" data-sentry-source-file="FileList.tsx">
            {t("code.fileListHeaderFileName")}
          </Typography>
        </Grid>
        <Grid size={4} data-sentry-element="Grid" data-sentry-source-file="FileList.tsx">
          <Typography variant="h6" fontWeight={700} color="common.white" data-sentry-element="Typography" data-sentry-source-file="FileList.tsx">
            {t("code.fileListHeaderLastSaved")}
          </Typography>
        </Grid>
      </Grid>
    </Box>;
};
export const FileList: FC<FileListProps> = ({
  files = [],
  searchKeyword,
  searchInputRef,
  onClearSearch
}) => {
  const filteredFiles = useMemo(() => {
    if (!searchKeyword) return files;
    const searchTerm = searchKeyword.toLowerCase();
    return files.filter(file => {
      const searchString = [file.fileName, file.path, file.ZUID, file.fileType, file.type].filter(Boolean).join("\n").toLowerCase();
      return searchString.includes(searchTerm);
    });
  }, [files, searchKeyword]);
  const handleClearSearch = () => {
    onClearSearch?.();
    searchInputRef?.current?.focus();
  };
  return <Paper variant="outlined" sx={{
    display: "flex",
    flexDirection: "column",
    bgcolor: "inherit",
    color: "grey.400",
    borderColor: "grey.700",
    borderRadius: 2,
    height: "100%",
    overflow: "hidden"
  }} data-sentry-element="Paper" data-sentry-component="FileList" data-sentry-source-file="FileList.tsx">
      <FileListHeader data-sentry-element="FileListHeader" data-sentry-source-file="FileList.tsx" />

      <Box height="calc(100% - 56px)" overflow="auto" data-cy="AllFilesTable" data-sentry-element="Box" data-sentry-source-file="FileList.tsx">
        {filteredFiles.length > 0 ? filteredFiles.map((file, index) => <FileRowItem key={file.path || file.ZUID || index} {...file} isLast={index === filteredFiles.length - 1} />) : <Box data-cy="NoResultsContainer" height="100%" width="100%" sx={{
        display: "grid",
        placeContent: "center"
      }}>
            <NoResults type="search" onButtonClick={handleClearSearch} searchTerm={searchKeyword} sx={{
          "& img": {
            height: 200,
            width: 220
          },
          "& h4.MuiTypography-root": {
            color: "common.white"
          },
          "& p.MuiTypography-root": {
            color: "grey.200"
          }
        }} />
          </Box>}
      </Box>
    </Paper>;
};